import React, { useEffect, useRef, useState } from 'react';

import { useFormik } from 'formik';
import _ from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { createSurvey } from 'api/endpoints/survey';
import { uploadImages } from 'api/endpoints/upload';
import Breadcrumb from 'components/Breadcrumb';
import Button from 'components/Button';
import LayoutReview from 'components/layouts/LayoutReview';
import UndoRedo from 'components/UndoRedo';
import { ENUM_SURVEY_STATUS, ENUM_SURVEY_TYPE, ENUM_TYPE_ACTION } from 'constants/enum';
import LOCAL_STORAGE_KEYS from 'constants/localStorageKeys';
import useChangeRouter from 'hooks/useChangeRouter';
import { useClickOutside } from 'hooks/useClickOutside';
import useReloadPage from 'hooks/useReloadPage';
import { toastError, toastSuccess } from 'lib/toast';
import AppScreenReview from 'pages/Survey/_components/AppScreenReview';
import Form from 'pages/Survey/Form/Form';
import AppRoutes from 'routes/routes';
import { clearModal, setLoading, setModal } from 'store/actions/common';
import { clearCreateSurvey, setHistoryCreateSurvey } from 'store/actions/survey';
import { base64ToFile, cleanDataSurvey, scrollToPosition, scrollToSection } from 'utils/helper';
import { removeFromLocalStorage } from 'utils/localStorage';

const CreateSurvey = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [sectionFocusing, setSectionFocusing] = useState(-1);
  const [questionFocusing, setQuestionFocusing] = useState({});

  const controlSurveyRef = useRef(null);
  const [openControlSurvey, setIsOpenControlSurvey] = useState(false);
  const { history_create_survey } = useSelector((state) => state.survey);

  useReloadPage('', history_create_survey && history_create_survey.length > 1);

  useClickOutside(controlSurveyRef, () => setIsOpenControlSurvey(false));
  const { goto } = useChangeRouter();

  const navigator = useNavigate();
  const dispatch = useDispatch();

  const { me } = useSelector((state) => state.auth);

  const initialValues = (() => {
    const initData = {
      title: '',
      description: '',
      status: ENUM_SURVEY_STATUS.DRAFT,
      survey_type: ENUM_SURVEY_TYPE.EXAM,
      period_type: null,
      sections: [
        {
          id: 1,
          title: '',
          order: 0,
          action: ENUM_TYPE_ACTION.CREATE,
          questions: [],
        },
      ],
    };

    if (history_create_survey.length === 0) dispatch(setHistoryCreateSurvey([JSON.stringify(initData)]));
    return initData;
  })();

  const handleUploadImage = async (values) => {
    /**
     * Handle image upload
     */
    const formData = new FormData();
    const section_ids = [];
    const question_ids = [];
    await values.sections.map(async (section, sectionIndex) => {
      await section.questions.map(async (question, questionIndex) => {
        if (question.images?.length > 0) {
          const file = await base64ToFile(question.images[0].image_url, `s-${sectionIndex}-q-${questionIndex}-${moment().unix()}`);
          formData.append('files', file);
          section_ids.push(section.id);
          question_ids.push(question.id);
        }
      });
    });

    let images = [];
    if (section_ids.length > 0) {
      formData.append('section_ids', section_ids.join(','));
      formData.append('question_ids', question_ids.join(','));
      images = await uploadImages(formData)
        .then(async (response) => {
          return response.data;
        })
        .catch(() => {
          return [];
        });
    }

    return images;
  };

  const handleCreateSurvey = async (images, values) => {
    const handleGetImages = (question, sectionId, questionId) => {
      return [
        {
          bucket_name: _.find(images, (img) => img.section_index === sectionId && img.question_index === questionId)?.bucket_name,
          image_url: _.find(images, (img) => img.section_index === sectionId && img.question_index === questionId)?.blob_name,
          image_layout: question?.images[0]?.image_layout,
        },
      ];
    };

    /**
     * Handle create survey
     */
    const data = {
      ...values,
      admin_id: me.id,
      sections: values.sections.map((section, sectionIndex) => {
        return {
          ...section,
          order: sectionIndex,
          questions: section.questions.map((question, questionIndex) => {
            return {
              ...question,
              order: questionIndex,
              ...(question.images && question.images.length > 0
                ? {
                    images: handleGetImages(question, section.id, question.id),
                  }
                : {}),
            };
          }),
        };
      }),
    };

    const handleScrollToSectionError = (field_names) => {
      const firstError = field_names[0]?.replace(/body\./g, '');

      function scrollToIdentifier(prefix, identifier) {
        scrollToSection(`${prefix}-${identifier}`);
      }

      // Check for 'questions' or 'sections' and scroll accordingly
      const identifyAndScroll = (error, id, prefixMap) => {
        let idString = '';
        prefixMap.map((prefix) => {
          const start = error.indexOf(prefix) + `${prefix}.`.length;
          const end = error.indexOf('.', start);
          idString += error.substring(start, end);
        });

        scrollToIdentifier(id, idString);
        return true;
      };

      if (firstError) {
        // body.sections.0.questions.0.title
        if (firstError.includes('questions.')) {
          return identifyAndScroll(firstError, 'questions', ['sections', 'questions']);
        }

        // body.sections.1.title
        // body.sections.1.questions
        if (firstError.includes('sections')) {
          return identifyAndScroll(firstError, 'sections', ['sections']);
        }

        if (firstError.includes('title')) {
          scrollToPosition(0);
          return true;
        }
      }
    };

    await createSurvey({
      ...data,
      status: ENUM_SURVEY_STATUS.DRAFT,
    })
      .then((response) => {
        removeFromLocalStorage(LOCAL_STORAGE_KEYS.HISTORY_CREATE_SURVEY_FORM);
        if (data.status === ENUM_SURVEY_STATUS.PUBLIC) {
          navigator(AppRoutes.surveyViewFn(response.data.survey_id), { state: { openSchedule: true } });
        } else {
          toastSuccess(response.message);
          navigator(AppRoutes.survey);
        }
      })
      .catch((error) => {
        toastError(error.message);
        if (error.field_names) {
          handleScrollToSectionError(error?.field_names || []);
          error.field_names?.map((errorField) => {
            form.setFieldError(errorField.replace(/body./g, ''), true);
          });
        }
      });
  };

  const form = useFormik({
    initialValues: initialValues,
    validateOnChange: false,
    validateOnBlur: false,
    validate: (values) => {
      const errors = {};
      if (values.status === ENUM_SURVEY_STATUS.PUBLIC && !values.period_type) errors.period_type = true;
      return errors;
    },
    onSubmit: async (values) => {
      try {
        const newValues = cleanDataSurvey(values);
        dispatch(setLoading({ isOpen: true }));
        const images = await handleUploadImage(newValues);
        await handleCreateSurvey(images, newValues);
        dispatch(setLoading({ isOpen: false }));
      } catch {
        dispatch(setLoading({ isOpen: false }));
      }
    },
  });

  const onClickSave = (status) => {
    form.setFieldValue('status', status);
    form.values.status = status;
    form.handleSubmit();
  };

  const onClickDelete = ({ message = t('CONFIRM_DELETE_CREATE_SURVEY') }) => {
    const confirmDelete = () => {
      dispatch(clearCreateSurvey());
      form.setValues(initialValues);
      dispatch(clearModal());
    };

    dispatch(
      setModal({
        isOpen: true,
        type: 'delete',
        title: message,
        confirmText: t('DELETE'),
        cancelText: t('CANCEL_TEXT'),
        onConfirm: confirmDelete,
        onCancel: () => {
          dispatch(clearModal());
        },
      })
    );
  };

  const breadcrumbs = [
    {
      title: t('SURVEY_LIST'),
      event: () => goto(AppRoutes.home),
    },
    {
      title: t('NEW_SURVEY'),
    },
  ];

  useEffect(() => {
    if (location.state?.period_type) {
      form.setFieldValue('period_type', location.state.period_type);
    }

    navigator(location.pathname, { replace: true });
    return () => {
      dispatch(clearCreateSurvey());
    };
  }, []);

  const handleClickUndoRedo = () => {
    setSectionFocusing(-1);
    setQuestionFocusing({});
  };

  return (
    <section id="create-survey" className="flex-middle-start gap-6 relative">
      <LayoutReview className="z-2">
        <form className="create-survey">
          <div id="header-form" className="flex-between-center mb-8">
            <div className="flex items-center gap-2">
              <Breadcrumb breadcrumbs={breadcrumbs} />
            </div>

            <div className="flex items-center gap-8">
              <UndoRedo onClick={handleClickUndoRedo} form={form} />

              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  color="outline"
                  className="text-black/85 text-sm w-20 h-8"
                  onClick={() => onClickSave(ENUM_SURVEY_STATUS.PUBLIC)}
                  text={t('SAVE_PUBLIC')}
                />
                <Button
                  type="button"
                  color="primary"
                  className="text-sm w-20 h-8"
                  onClick={() => onClickSave(ENUM_SURVEY_STATUS.DRAFT)}
                  text={t('SAVE')}
                />

                <div className="relative flex items-center">
                  <button type="button" className="w-6 h-8" onClick={() => setIsOpenControlSurvey((prev) => !prev)} ref={controlSurveyRef}>
                    <img src={require('assets/resources/MoreVert.svg').default} alt="more vert icon" width={24} height={24} />
                  </button>
                  <div className={`absolute right-0 py-1 bg-white shadow-soft-md animation-dropdown ${openControlSurvey ? 'show' : ''}`}>
                    <ul>
                      <li
                        onClick={onClickDelete}
                        className="px-3 w-20 py-1.75 text-danger-229 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
                      >
                        {t('DELETE')}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Form
            form={form}
            sectionFocusing={sectionFocusing}
            setSectionFocusing={setSectionFocusing}
            questionFocusing={questionFocusing}
            setQuestionFocusing={setQuestionFocusing}
          />
        </form>
      </LayoutReview>
      <div className="sticky top-header-padding">
        <AppScreenReview form={form} />
      </div>
    </section>
  );
};

export default React.memo(CreateSurvey);
